<template>
  <main class="director-page">
    <rh-cabecalho
      pagina="inicio"
      :mostrar-apresentacao="false"
      :unidade-id="unidadeId"
      :empresas="empresas"
      :mostrar-pessoas="true"
      :mostrar-configuracao="true"
      @empresa-selecionada="selecionarEmpresa"
    />

    <b-message v-if="erro" type="is-danger" aria-close-label="Fechar" @close="erro = ''">{{ erro }}</b-message>
    <b-loading :active="loading" :is-full-page="false"></b-loading>

    <section class="welcome">
      <div>
        <p class="welcome-kicker">VISÃO DA DIRETORIA</p>
        <h1>Olá, {{ primeiroNome }}<span>.</span></h1>
        <p>Uma leitura clara do que acontece na empresa para orientar suas próximas decisões.</p>
      </div>

    </section>

    <div v-if="!loading && !empresas.length" class="empty-state">
      Nenhuma empresa vinculada ao seu acesso. Peça ao administrador para configurar sua unidade.
    </div>

    <template v-if="unidadeId">
      <section class="overview-section">
        <div class="section-heading">
          <div>
            <p class="eyebrow">PANORAMA DA EMPRESA</p>
            <h2>A empresa numa só tela</h2>
            <p>Indicadores da empresa selecionada, atualizados a partir dos ciclos e resultados disponíveis.</p>
          </div>
          <router-link class="text-link" to="/rh/diagnostico">Abrir diagnóstico <b-icon icon="arrow-right" size="is-small" /></router-link>
        </div>

        <div class="stat-grid">
          <div class="stat-card">
            <span class="stat-icon violet"><b-icon icon="chart-bar" /></span>
            <span class="stat-label">Índice divulgado</span>
            <strong>{{ mediaEmpresa === null ? '—' : formatarNota(mediaEmpresa) }}</strong>
            <small>Média ponderada das áreas com resultado</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon blue"><b-icon icon="account-group-outline" /></span>
            <span class="stat-label">Equipes</span>
            <strong>{{ resumoDisponivel ? resumo.equipes : '—' }}</strong>
            <small>{{ resumoDisponivel ? `${resumo.areas} áreas na empresa` : 'Aguardando dados da empresa' }}</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon peach"><b-icon icon="clipboard-text-outline" /></span>
            <span class="stat-label">Ciclos abertos</span>
            <strong>{{ ciclosDisponiveis ? ciclosAbertos.length : '—' }}</strong>
            <small>Coleta ou início programado</small>
          </div>
          <div class="stat-card">
            <span class="stat-icon mint"><b-icon icon="shield-check-outline" /></span>
            <span class="stat-label">Áreas com resultado</span>
            <strong>{{ mapa ? `${areasDivulgadas} / ${mapa.areas.length}` : '—' }}</strong>
            <small>Somente ciclos divulgáveis</small>
          </div>
        </div>
      </section>

      <section class="heat-card">
        <div class="heat-heading">
          <div>
            <p class="eyebrow">MAPA DE CALOR</p>
            <h2>Áreas × dimensões do ambiente</h2>
            <p>Médias dos ciclos encerrados mais recentes de cada equipe com respostas suficientes.</p>
          </div>
          <div v-if="mapa" class="heat-legend">
            <span><i class="critical"></i> Crítico</span>
            <span><i class="attention"></i> Atenção</span>
            <span><i class="healthy"></i> Saudável</span>
          </div>
        </div>
        <div v-if="mapa && mapa.areas.length" class="heat-scroll">
          <table class="heat-table">
            <thead>
              <tr>
                <th scope="col">Área</th>
                <th v-for="dimensao in mapa.dimensoes" :key="dimensao.id" scope="col">{{ dimensao.nome }}</th>
                <th scope="col">Média</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="area in mapa.areas" :key="area.areaId">
                <th scope="row">
                  <strong>{{ area.area }}</strong>
                  <small v-if="area.nomesEquipes && area.nomesEquipes.length" class="heat-team-names">{{ area.nomesEquipes.join(', ') }}</small>
                  <small v-if="area.equipes">{{ area.equipes }} {{ area.equipes === 1 ? 'equipe' : 'equipes' }} · {{ area.respostas }} respostas</small>
                  <small v-else>Sem resultado divulgado</small>
                </th>
                <td v-for="dimensao in mapa.dimensoes" :key="dimensao.id">
                  <span v-if="notaArea(area, dimensao.id) !== null" class="heat-cell" :class="classeNota(notaArea(area, dimensao.id))">{{ formatarNota(notaArea(area, dimensao.id)) }}</span>
                  <span v-else class="heat-empty">—</span>
                </td>
                <td><span v-if="mediaArea(area) !== null" class="heat-index">{{ formatarNota(mediaArea(area)) }}</span><span v-else class="heat-empty">—</span></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="!loading" class="heat-empty-state">
          <b-icon icon="shield-lock-outline" size="is-medium" />
          <p>{{ erroMapa || 'O mapa aparecerá quando houver áreas com resultados divulgados.' }}</p>
          <router-link v-if="!mapa" to="/rh/configuracao">Ver configuração</router-link>
        </div>
        <p v-if="mapa" class="heat-footnote">Áreas sem nota permanecem ocultas até o encerramento dos ciclos e o mínimo de respostas configurado.</p>
      </section>

      <div class="lower-grid">
        <section class="cycles-card">
          <div class="card-heading">
            <div>
              <p class="eyebrow">COLETAS EM ANDAMENTO</p>
              <h2>Ciclos abertos</h2>
              <p>O andamento das equipes em uma única lista.</p>
            </div>
            <router-link class="text-link" to="/rh/diagnostico">Ver ciclos <b-icon icon="arrow-right" size="is-small" /></router-link>
          </div>
          <div v-if="ciclosDisponiveis && ciclosAbertos.length" class="cycle-list">
            <article v-for="ciclo in ciclosAbertos.slice(0, 6)" :key="ciclo.id" class="cycle-row">
              <div class="cycle-icon"><b-icon icon="calendar-check-outline" /></div>
              <div class="cycle-copy">
                <strong>{{ ciclo.equipe }}</strong>
                <span>{{ ciclo.nome }}<template v-if="ciclo.lider"> · {{ ciclo.lider }}</template></span>
                <small>{{ ciclo.ativo ? 'Em coleta' : 'Programado' }} · {{ ciclo.respostas }} {{ ciclo.respostas === 1 ? 'resposta' : 'respostas' }} · até {{ dataCurta(ciclo.fimEm) }}</small>
              </div>
              <span class="cycle-status" :class="{ planned: !ciclo.ativo }">{{ ciclo.ativo ? 'Aberto' : 'Em breve' }}</span>
            </article>
            <p v-if="ciclosAbertos.length > 6" class="more-cycles">Mais {{ ciclosAbertos.length - 6 }} ciclos na tela Diagnóstico.</p>
          </div>
          <div v-else-if="ciclosDisponiveis && !loading" class="empty-state">Nenhum ciclo aberto no momento.</div>
          <div v-else-if="!loading" class="empty-state">Não foi possível carregar os ciclos da empresa agora.</div>
        </section>

        <aside class="priority-card">
          <p class="eyebrow">ONDE OLHAR PRIMEIRO</p>
          <h2>Pontos de atenção</h2>
          <p class="priority-intro">As menores médias divulgadas por área e dimensão.</p>
          <div v-if="prioridades.length" class="priority-list">
            <div v-for="(item, indice) in prioridades" :key="`${item.areaId}-${item.dimensaoId}`" class="priority-row">
              <span class="priority-number">0{{ indice + 1 }}</span>
              <div><strong>{{ item.area }}</strong><small>{{ item.dimensao }}</small></div>
              <b-tag :type="tipoNota(item.nota)">{{ formatarNota(item.nota) }}</b-tag>
            </div>
          </div>
          <div v-else class="priority-empty">{{ areasDivulgadas ? 'Nenhuma média divulgada está abaixo do corte saudável.' : 'Os pontos de atenção aparecerão quando houver resultados divulgados.' }}</div>
          <router-link class="priority-action" to="/rh/diagnostico">Explorar resultados <b-icon icon="arrow-right" size="is-small" /></router-link>
        </aside>
      </div>
    </template>
  </main>
</template>

<script>
import http from '@/http'
import RhCabecalho from '@/components/rh_cabecalho.vue'

export default {
  name: 'RhDashboardDiretoria',
  components: { RhCabecalho },
  data() {
    return {
      empresas: [],
      unidadeId: null,
      resumo: { areas: 0, equipes: 0, ciclosAbertos: 0 },
      equipes: [],
      ciclos: [],
      mapa: null,
      resumoDisponivel: false,
      ciclosDisponiveis: false,
      erroMapa: '',
      loading: false,
      erro: '',
      requisicao: 0,
    }
  },
  computed: {
    primeiroNome() {
      const usuario = this.$store.state.usuario || {}
      const nome = usuario.apelido || usuario.funcionarioNome || usuario.nomeCadastro || usuario.nome || 'você'
      return String(nome).trim().split(/\s+/)[0]
    },
    empresaAtual() {
      return this.empresas.find((empresa) => empresa.id === this.unidadeId) || null
    },
    ciclosAbertos() {
      return this.ciclos.filter((ciclo) => ciclo.aberto && new Date(ciclo.fimEm).getTime() > Date.now())
        .sort((a, b) => new Date(a.fimEm) - new Date(b.fimEm))
    },
    areasDivulgadas() {
      return this.mapa ? this.mapa.areas.filter((area) => this.mediaArea(area) !== null).length : 0
    },
    mediaEmpresa() {
      if (!this.mapa) return null
      const areas = this.mapa.areas.filter((area) => this.mediaArea(area) !== null && area.respostas > 0)
      const respostas = areas.reduce((total, area) => total + area.respostas, 0)
      return respostas ? areas.reduce((total, area) => total + this.mediaArea(area) * area.respostas, 0) / respostas : null
    },
    prioridades() {
      if (!this.mapa) return []
      return this.mapa.areas.flatMap((area) => this.mapa.dimensoes.map((dimensao) => ({
        areaId: area.areaId,
        area: area.area,
        dimensaoId: dimensao.id,
        dimensao: dimensao.nome,
        nota: this.notaArea(area, dimensao.id),
      }))).filter((item) => item.nota !== null && item.nota < this.mapa.corteSaudavel)
        .sort((a, b) => a.nota - b.nota).slice(0, 3)
    },
  },
  created() {
    this.iniciar()
  },
  methods: {
    dataCurta(valor) {
      return valor ? new Date(valor).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
    },
    formatarNota(valor) {
      return Number(valor).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    notaArea(area, dimensaoId) {
      const valor = area.notas && area.notas[dimensaoId]
      return valor === null || valor === undefined ? null : Number(valor)
    },
    mediaArea(area) {
      if (!this.mapa) return null
      const notas = this.mapa.dimensoes.map((dimensao) => this.notaArea(area, dimensao.id)).filter((nota) => nota !== null)
      return notas.length ? notas.reduce((total, nota) => total + nota, 0) / notas.length : null
    },
    classeNota(nota) {
      if (!this.mapa) return ''
      return nota < this.mapa.corteCritico ? 'critical' : nota < this.mapa.corteSaudavel ? 'attention' : 'healthy'
    },
    tipoNota(nota) {
      return this.classeNota(nota) === 'critical' ? 'is-danger' : this.classeNota(nota) === 'attention' ? 'is-warning' : 'is-success'
    },
    selecionarEmpresa(id) {
      this.unidadeId = id
      this.carregar()
    },
    async iniciar() {
      this.loading = true
      try {
        this.empresas = (await http.get('/api/rh/estrutura/empresas')).data || []
        const atual = Number(this.$store.state.unidadeId)
        this.unidadeId = this.empresas.some((empresa) => empresa.id === atual)
          ? atual
          : (this.empresas[0] && this.empresas[0].id) || null
        if (this.unidadeId) await this.carregar()
      } catch (e) {
        this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar seu painel.'
      } finally {
        this.loading = false
      }
    },
    async carregar() {
      const requisicao = ++this.requisicao
      const unidadeId = this.unidadeId
      this.loading = true
      this.erro = ''
      this.erroMapa = ''
      this.resumoDisponivel = false
      this.ciclosDisponiveis = false
      this.resumo = { areas: 0, equipes: 0, ciclosAbertos: 0 }
      this.equipes = []
      this.ciclos = []
      this.mapa = null
      if (!unidadeId) {
        this.loading = false
        return
      }
      try {
        const params = { unidadeId }
        const [resumo, equipes, mapa] = await Promise.allSettled([
          http.get('/api/rh/estrutura/resumo', { params }),
          http.get('/api/rh/estrutura/equipes', { params }),
          http.get('/api/rh/diagnostico/mapa-calor', { params }),
        ])
        if (requisicao !== this.requisicao) return
        if (resumo.status === 'fulfilled') {
          this.resumo = resumo.value.data || this.resumo
          this.resumoDisponivel = true
        }
        if (mapa.status === 'fulfilled') this.mapa = mapa.value.data
        else this.erroMapa = mapa.reason && mapa.reason.data && mapa.reason.data.erro
          ? mapa.reason.data.erro : 'O mapa de calor não pôde ser carregado agora.'
        if (resumo.status === 'rejected' || equipes.status === 'rejected')
          this.erro = 'Parte das informações da empresa não pôde ser carregada. Tente atualizar a página.'

        if (equipes.status === 'fulfilled') {
          this.equipes = equipes.value.data || []
          const respostas = await Promise.allSettled(this.equipes.map((equipe) =>
            http.get('/api/rh/diagnostico/ciclos', { params: { unidadeId, equipeId: equipe.id } })
          ))
          if (requisicao !== this.requisicao) return
          this.ciclosDisponiveis = respostas.every((resposta) => resposta.status === 'fulfilled')
          if (this.ciclosDisponiveis) {
            this.ciclos = respostas.flatMap((resposta, indice) =>
              (resposta.value.data || []).map((ciclo) => ({ ...ciclo, equipe: this.equipes[indice].nome, lider: this.equipes[indice].lider }))
            )
          } else {
            this.erro = 'Não foi possível carregar os ciclos de todas as equipes.'
          }
        }
      } catch (e) {
        if (requisicao === this.requisicao)
          this.erro = e && e.data && e.data.erro ? e.data.erro : 'Não foi possível carregar seu painel.'
      } finally {
        if (requisicao === this.requisicao) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.director-page { --navy: #0c193a; --blue: #087cf4; --ink: #17223c; background: #f7f8fc; color: var(--ink); font-family: Montserrat, Arial, sans-serif; min-height: 100vh; padding: 0 28px 64px; border-radius: 18px; }
.welcome { display: flex; justify-content: space-between; align-items: center; gap: 30px; color: #fff; background: radial-gradient(circle at 85% 10%, #1b437c 0, transparent 34%), linear-gradient(125deg, #0c193a, #10234e); border-radius: 0 0 30px 30px; padding: 40px 44px; }
.welcome-kicker { color: #9dc3ff; font-size: 11px; font-weight: 800; letter-spacing: .16em; margin: 0 0 8px; }
.welcome h1 { color: #fff; font-size: clamp(34px, 4vw, 56px); line-height: 1.1; letter-spacing: -.04em; margin: 0 0 12px; font-weight: 800; }
.welcome h1 span { color: #55c7dd; }
.welcome > div > p:last-child { color: #d8e6ff; font-size: 15px; margin: 0; max-width: 580px; }
.welcome-company { display: flex; align-items: center; gap: 15px; min-width: 275px; background: rgba(255,255,255,.09); border: 1px solid rgba(188,213,255,.24); border-radius: 20px; padding: 17px 20px; }
.company-icon { color: #a9d5ff; background: rgba(255,255,255,.1); border-radius: 12px; padding: 10px; }
.welcome-company div { display: flex; flex-direction: column; gap: 2px; }
.welcome-company small { color: #a7caff; font-size: 10px; font-weight: 800; letter-spacing: .12em; }
.welcome-company strong { color: #fff; font-size: 17px; }
.welcome-company div > span { color: #c5d9f5; font-size: 12px; }
.overview-section { margin-top: 34px; }
.section-heading, .card-heading, .heat-heading { display: flex; justify-content: space-between; align-items: flex-end; gap: 20px; margin-bottom: 21px; }
.eyebrow { color: #2280ca; font-size: 11px; font-weight: 800; letter-spacing: .16em; margin: 0 0 6px; }
.section-heading h2, .card-heading h2 { color: var(--navy); font-size: clamp(23px, 2.6vw, 32px); font-weight: 800; line-height: 1.2; margin: 0 0 5px; }
.section-heading p:last-child, .card-heading p:last-child { color: #6b778e; font-size: 13px; margin: 0; }
.text-link { color: #126fd3; font-size: 12px; font-weight: 800; white-space: nowrap; }
.text-link:hover { color: #0a4390; }
.text-link .icon, .priority-action .icon { vertical-align: middle; }
.stat-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.stat-card { display: flex; flex-direction: column; min-height: 180px; background: #fff; border: 1px solid #e3e8f0; border-radius: 20px; padding: 22px; box-shadow: 0 10px 24px rgba(16,32,68,.04); }
.stat-icon { display: grid; place-items: center; width: 39px; height: 39px; border-radius: 12px; margin-bottom: 15px; }
.stat-icon.violet { color: #714be2; background: #efeaff; } .stat-icon.blue { color: #1b76d9; background: #e5f1ff; } .stat-icon.peach { color: #bd6a27; background: #fff0e1; } .stat-icon.mint { color: #168b7c; background: #ddf8f1; }
.stat-label { color: #617087; font-size: 12px; font-weight: 700; }
.stat-card strong { color: var(--navy); font-size: 32px; line-height: 1.15; font-weight: 800; margin: 5px 0; }
.stat-card small { color: #778398; font-size: 11px; line-height: 1.4; }
.heat-card { color: #fff; background: #0e1d44; border-radius: 24px; padding: 30px; margin-top: 25px; }
.heat-card .eyebrow { color: #9dc3ff; }
.heat-heading { align-items: flex-end; margin-bottom: 22px; }
.heat-heading h2 { color: #fff; font-size: clamp(21px, 2.4vw, 29px); font-weight: 800; margin: 0 0 5px; }
.heat-heading p:last-child { color: #b6c9e9; font-size: 12px; margin: 0; }
.heat-legend { display: flex; flex-wrap: wrap; gap: 12px; color: #d2dff4; font-size: 11px; white-space: nowrap; }
.heat-legend span { display: flex; align-items: center; gap: 5px; }
.heat-legend i { display: inline-block; width: 9px; height: 9px; border-radius: 3px; }
.heat-legend i.critical, .heat-cell.critical { background: #ee786c; } .heat-legend i.attention, .heat-cell.attention { background: #f4c452; } .heat-legend i.healthy, .heat-cell.healthy { background: #51c5ae; }
.heat-scroll { overflow-x: auto; }
.heat-table { width: 100%; min-width: 1000px; border-collapse: separate; border-spacing: 5px 8px; }
.heat-table th, .heat-table td { text-align: center; vertical-align: middle; }
.heat-table thead th { color: #a9c8ff; font-size: 10px; font-weight: 700; padding: 0 3px 5px; line-height: 1.25; }
.heat-table th:first-child { width: 210px; text-align: left; }
.heat-table tbody th { color: #fff; font-size: 12px; line-height: 1.3; }
.heat-table tbody th strong, .heat-table tbody th small { display: block; }
.heat-table tbody th small { color: #a8c6f0; font-size: 10px; font-weight: 500; margin-top: 3px; }
.heat-table tbody th .heat-team-names { color: #dceaff; font-size: 11px; overflow-wrap: anywhere; }
.heat-cell, .heat-index, .heat-empty { display: grid; place-items: center; min-width: 62px; min-height: 38px; border-radius: 8px; font-size: 11px; font-weight: 800; }
.heat-cell { color: #102044; } .heat-index { color: #fff; background: #263e6b; } .heat-empty { color: #adc0de; background: #1a2c53; }
.heat-footnote { color: #9eb5d7; font-size: 11px; margin: 15px 0 0; }
.heat-empty-state { display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; min-height: 180px; color: #c2d4ee; text-align: center; border: 1px dashed #45618d; border-radius: 15px; padding: 20px; }
.heat-empty-state p { margin: 0; } .heat-empty-state a { color: #8ed5ff; font-weight: 700; }
.lower-grid { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(300px, .75fr); gap: 18px; margin-top: 24px; }
.cycles-card { background: #fff; border: 1px solid #e3e8f0; border-radius: 22px; padding: 28px; box-shadow: 0 10px 24px rgba(16,32,68,.04); min-width: 0; }
.card-heading { align-items: start; }
.card-heading h2 { font-size: 23px; }
.cycle-list { display: grid; }
.cycle-row { display: flex; align-items: center; gap: 13px; border-top: 1px solid #ebeff5; padding: 16px 0; }
.cycle-icon { display: grid; place-items: center; width: 40px; height: 40px; flex: none; color: #1b76d9; background: #e5f1ff; border-radius: 12px; }
.cycle-copy { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.cycle-copy strong { color: var(--navy); font-size: 13px; }
.cycle-copy span { color: #4c5b75; font-size: 12px; }
.cycle-copy small { color: #8591a3; font-size: 11px; }
.cycle-status { color: #086f5e; background: #dff7f0; border-radius: 30px; padding: 7px 10px; font-size: 10px; font-weight: 800; white-space: nowrap; }
.cycle-status.planned { color: #815b16; background: #fff0cf; }
.more-cycles { color: #687994; font-size: 11px; margin: 7px 0 0; }
.priority-card { display: flex; flex-direction: column; color: #fff; background: #0e1d44; border-radius: 22px; padding: 28px; }
.priority-card .eyebrow { color: #9dc3ff; }
.priority-card h2 { color: #fff; font-size: 23px; font-weight: 800; margin: 0 0 5px; }
.priority-intro { color: #b6c9e9; font-size: 12px; margin: 0 0 15px; }
.priority-list { display: grid; }
.priority-row { display: flex; align-items: center; gap: 13px; border-top: 1px solid #30466d; padding: 16px 0; }
.priority-number { color: #7dbbff; font-size: 12px; font-weight: 800; }
.priority-row div { display: flex; flex-direction: column; flex: 1; min-width: 0; }
.priority-row strong { color: #fff; font-size: 13px; } .priority-row small { color: #adc3e5; font-size: 11px; }
.priority-empty { color: #c4d3eb; font-size: 12px; line-height: 1.6; margin-top: 10px; }
.priority-action { color: #fff; background: #087cf4; border-radius: 13px; text-align: center; padding: 13px 15px; font-size: 12px; font-weight: 800; margin-top: auto; }
.priority-action:hover { color: #fff; background: #2991ff; }
.empty-state { color: #66748a; background: #fff; border: 1px dashed #cfd8e6; border-radius: 18px; padding: 25px; text-align: center; font-size: 14px; }
@media (max-width: 1050px) { .stat-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } .lower-grid { grid-template-columns: 1fr; } }
@media (max-width: 700px) { .welcome { flex-direction: column; align-items: stretch; padding: 27px 22px; } .welcome-company { min-width: 0; } .section-heading, .card-heading, .heat-heading { flex-direction: column; align-items: flex-start; } }
@media (max-width: 600px) { .director-page { padding: 0 14px 36px; } .welcome { border-radius: 0 0 26px 26px; } .stat-grid { gap: 10px; } .stat-card { min-height: 165px; padding: 16px; } .heat-card, .cycles-card, .priority-card { padding: 20px; } }
@media (max-width: 380px) { .stat-grid { grid-template-columns: 1fr; } }
</style>
